import { KompisDefaultFields } from '@inter-ikea-kompis/services';
import { applicationSettings } from '../../settings/application';

import { getProductService } from '../ServiceHandler';
import productsService from '.';
import {
  setProducts,
  validateProducts,
  getMissingMandatoryProductCategories,
  flagSuperfluousProductsAsInvalid,
} from './productHandler';
import { setArticles } from './articles';
import { init as initConnections } from './models';

import React from 'react';
import ReactDOM from 'react-dom';
import FakeData from '../../components/FakeData';
import localStatisticsReporter from '../statistics/insights/custom/local/localStatisticsReporter';
import { UrlUtility } from '../../util/aactools/urlUtility';
import { useKioskIntegration } from '../../hooks/useKioskIntegration';

const urlUtility = new UrlUtility();

const allowFakeDataLoading = !applicationSettings.isProd;

const unique = (item, pos, arr) => arr.indexOf(item) === pos;

// TODO: We might need to adapt and re-add this if we need to use future token in the future.
/*const getLocalizationOverrides = () => {
  const futureToken =
    !applicationSettings.isProd && urlUtility.getCombinedQuery().futureToken;

  if (futureToken) {
    return {
      localizationOverrides: {
        endpoints: {
          dexfWebplannerService: `https://api.dexf.qa.ikeadt.com/webplanner/v1/proxy/${futureToken}/items/retailunit/{retailUnit}/locale/{locale}`,
        },
      },
    };
  }
};*/

const getOptionalParams = () => {
  const futureDate =
    !applicationSettings.isProd && urlUtility.getCombinedQuery().futureDate;

  if (futureDate) {
    return {
      customParams: {
        timestamp: futureDate,
      },
    };
  }
};

function request(url, fallback = {}) {
  return fetch(url, { credentials: 'same-origin' })
    .then(res => res.json())
    .catch(err => {
      console.error(err);
      return fallback;
    });
}

function getProductSprs(products) {
  return products
    .reduce((sprs, prod) => sprs.concat(prod.sprno), [])
    .filter(unique)
    .map(id => ({ id, type: 'SPR' }));
}

function getProductItems(products) {
  return products
    .reduce((arts, art) => arts.concat(art.iows), [])
    .map(iow => iow.itemno)
    .filter(unique)
    .map(id => ({ id, type: 'ART' }));
}

async function getArticles(contents) {
  const productData = contents.find(o => o.hasOwnProperty('products'));
  const products = productData.products.filter(prod => prod.iows.length);
  const articles = getProductItems(products);
  const sprs = getProductSprs(products);

  const allItems = [...articles, ...sprs];
  if (allowFakeDataLoading) {
    const articles = JSON.parse(window.sessionStorage.getItem('FakeData'));
    if (articles) {
      setArticles(articles.filter(article => article.valid));
      return articles;
    }
  }

  return getProductService()
    .getProducts(allItems, KompisDefaultFields, getOptionalParams())
    .then(articles => {
      setArticles(articles.filter(article => article.valid));
      return articles;
    });
}

function createLoadFakeDataFunctionality(articles) {
  if (allowFakeDataLoading) {
    /**
     * Finds an article image of a desired size, based on an array of acceptable sizes
     * in descending priority order. If an image having highest prioritized size is not
     * found, the next to most prioritized size is looked for, etc.
     * @param {Object} articleContent The content object of the article.
     * @param {Array} sizePreferences An array with size preference strings ordered from most to least desired.
     * @returns {Object | null} An image object with an image of the highest prioritized size found, or null if
     *                          none of the desired sizes was found.
     */
    const findImageBySizePreferences = (articleContent, sizePreferences) => {
      let image = null;
      if (articleContent.image && sizePreferences.length) {
        image =
          articleContent.image.find(img => img.size === sizePreferences[0]) ||
          findImageBySizePreferences(articleContent, sizePreferences.slice(1));
      }
      return image;
    };

    const printIdAndImageInConsole = (data, size = 100) => {
      const boldStyle = 'font-weight: bold';
      const imgStyle =
        `font-size:${size}px; background:url(` +
        data.url +
        ') no-repeat; background-size: contain;';
      console.log(
        `%c${data.series} ${data.description}, ${data.design} (${data.type})`,
        boldStyle
      );
      console.log('ID:', data.id);
      console.log('Img URL: ' + data.url);
      console.log('%c  ', imgStyle);
    };

    const printAllIdsAndImagesInConsole = articles => {
      articles.forEach(article => {
        if (!article.content) return;
        const data = {
          series: article.content.name,
          description: article.content.typeName,
          design: article.content.validDesignText,
          type: article.content.itemType,
          url: findImageBySizePreferences(article.content, [
            'S5',
            'S4',
            'S3',
            'S2',
            'S1',
          ])?.url,
          id: article.content.ruItemNo,
        };
        printIdAndImageInConsole(data);
      });
    };

    window.parent.loadFakeData = function () {
      const appContainer = document.getElementById('root');
      const fakeDataContainer = document.createElement('div');
      appContainer.appendChild(fakeDataContainer);
      ReactDOM.render(<FakeData products={articles} />, fakeDataContainer);
      printAllIdsAndImagesInConsole(articles);
    };
  }
}

function reportLoadFailed(missingProductCategories) {
  localStatisticsReporter.reportNotLoaded(
    'articles_missing',
    missingProductCategories
  );
}

export default function loadProducts() {
  const files = [
    `configs/${applicationSettings.applicationName.toLowerCase()}/products.json`,
    `configs/${applicationSettings.applicationName.toLowerCase()}/models.json`,
  ];

  const { integration, isUpptackaOrIpexGallery } = useKioskIntegration();

  return Promise.all(files.map(request))
    .then(function (contents) {
      initConnections(contents);
      setProducts(contents);
      return getArticles(contents);
    })
    .then(articles => {
      createLoadFakeDataFunctionality(articles);
      validateProducts();
      const missingProductCategories = getMissingMandatoryProductCategories();
      if (missingProductCategories.length === 0) {
        flagSuperfluousProductsAsInvalid();
        productsService.makeArticleImages();
        return articles;
      } else {
        const errorMessage = 'Not enough products to build stuff';

        isUpptackaOrIpexGallery && integration?.missingProduct(errorMessage);
        reportLoadFailed(missingProductCategories);

        throw new Error(errorMessage);
      }
    });
}
